import { Component, Inject, Optional } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { describeReferralFailure, isPlausibleEmail } from '../../../../core/models/referral.model';
import { ReferralService } from '../../../../core/services/referral.service';

export interface ReferralInviteDialogResult {
  invited: boolean;
  alreadyInNetwork?: boolean;
  /** The address the invitation went to, for the page's message. */
  sentTo?: string;
}

export interface ReferralInviteDialogData {
  /** Practice-to-practice links are on: every invitation also carries the practice's referral code. */
  practiceLinksEnabled: boolean;
}

/**
 * Invite a medical professional: ONE path (owner, 2026-09-22; tasks/referral-invite-unified.md). The server decides
 * whether the address becomes a link request to another MedicAI practice's owner, the practice code by email, or an
 * invitation to MedicAI Referrals, and says only that it was sent. So this dialog never says which, nor whether the
 * address uses MedicAI (D5; POPIA). The one thing it is told is that the address is already in this practice.
 */
@Component({
  selector: 'app-referral-invite-dialog',
  templateUrl: './referral-invite-dialog.component.html',
  styleUrls: ['./referral-invite-dialog.component.scss'],
})
export class ReferralInviteDialogComponent {
  readonly form = this.fb.group({
    email: ['', [Validators.required, Validators.maxLength(320)]],
    name: ['', [Validators.maxLength(200)]],
    hpcsaNumber: ['', [Validators.maxLength(20)]],
  });
  isSaving = false;
  error: string | null = null;

  constructor(
    private fb: FormBuilder,
    private referralService: ReferralService,
    private dialogRef: MatDialogRef<ReferralInviteDialogComponent, ReferralInviteDialogResult>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: ReferralInviteDialogData | null,
  ) {
    this.form.controls.email.valueChanges.subscribe(() => { this.error = null; });
  }

  get emailInvalid(): boolean {
    const control = this.form.controls.email;
    return control.touched && !isPlausibleEmail(control.value);
  }

  get canSubmit(): boolean {
    return !this.isSaving && isPlausibleEmail(this.form.controls.email.value) && this.form.valid;
  }

  submit(): void {
    if (!this.canSubmit) {
      this.form.markAllAsTouched();
      return;
    }
    this.isSaving = true;
    this.error = null;
    const { email, name, hpcsaNumber } = this.form.getRawValue();
    const address = String(email).trim();
    this.referralService.invite({ email: address, name: name?.trim() || null, hpcsaNumber: hpcsaNumber?.trim() || null }).subscribe({
      next: (result) => this.dialogRef.close({ invited: true, alreadyInNetwork: result.alreadyInNetwork, sentTo: address }),
      error: (error) => {
        this.isSaving = false;
        this.error = describeReferralFailure(error, 'The invitation could not be sent. Please try again.');
      },
    });
  }

  cancel(): void {
    this.dialogRef.close({ invited: false });
  }
}
